import { useTranslations } from "../../../contexts/Translation";
import { useActionState, useRef } from "react";
import { useApiData } from "../../../contexts/ApiData";
import { ValidationError } from "../../../types";
import Input from "../Input";
import { H2 } from "../../Headers/Headings";
import Button from "../Button";
import Section from "../../Section";
import SystemMessage from "../../SystemMessage/Index";
import styles from "./ContactForm.module.css";
import formStyles from "../FormStyles.module.css";

type ContactFormStatus = "idle" | "success" | "serverError";
type ContactFormActionState = {
  error?: ValidationError;
  status: ContactFormStatus;
};

const getMessageError = (message: string, t: (key: string, ...args: string[]) => string): ValidationError | undefined => {
  if (message.trim().length > 0) {
    return undefined;
  }

  return { name: "message", message: t("messageError") };
};

const ContactForm = () => {
  const t = useTranslations("contactUsForm");
  const { post } = useApiData();
  const formRef = useRef<HTMLFormElement>(null);

  const submitAction = async (_previousState: ContactFormActionState, submittedFormData: FormData): Promise<ContactFormActionState> => {
    const message = String(submittedFormData.get("message") || "");
    const error = getMessageError(message, t);

    if (error) {
      return {
        error,
        status: "idle",
      };
    }

    const response = await post("/contact-us", { message });
    if (!response) {
      return {
        status: "serverError",
      };
    }

    formRef.current?.reset();
    return {
      status: "success",
    };
  };

  const [submissionState, formAction, pending] = useActionState(submitAction, { status: "idle" });

  if (submissionState.status === "serverError") {
    return <SystemMessage message={"contactFormError"} />;
  }

  return (
    <Section span={8}>
      <form ref={formRef} className={styles.form} action={formAction}>
        <H2>{t("heading")}</H2>
        <div className={`${formStyles.fieldRow} ${styles.inputWrapper}`}>
          <Input type='textarea' name='message' label={t("messageLabel")} error={submissionState.error} />
        </div>
        <Button disabled={pending}>{t("submit")}</Button>
        {submissionState.status === "success" && <p className={styles.receipt}>{t("receipt")}</p>}
      </form>
    </Section>
  );
};

export default ContactForm;
